import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, School } from '../services/authService';
import { stateService } from '../services/stateService';
import { extractRights, isTokenExpired } from '../utils/jwtUtils';
import {getErrorTranslation} from "../utils/errorUtils";
import {useTranslation} from "react-i18next";

interface AuthContextType {
  user: User | null;
  userType: string | null;
  schoolId: string | null;
  selectedSchool: School | null;
  rights: string[];
  login: (userName: string, password: string, schoolId?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  setSchoolId: (id: string | null, school?: School | null) => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [schoolId, setSchoolIdState] = useState<string | null>(null);
  const [selectedSchool, setSelectedSchoolState] = useState<School | null>(null);
  const [rights, setRights] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Reset selected school on initial load; it will be rehydrated next
    stateService.setSelectedSchool(null);
    setSelectedSchoolState(null);

    const token = authService.getToken();
    const storedUser = authService.getUser();
    const storedUserType = authService.getUserType();
    const storedSchoolId = localStorage.getItem('schoolId');

    // Check if token is expired
    if (token && isTokenExpired(token)) {
      // Token expired, logout user
      authService.logout();
      setUser(null);
      setUserType(null);
      setSchoolIdState(null);
      setSelectedSchoolState(null);
      setRights([]);
      stateService.setSelectedSchool(null);
      setLoading(false);
      return;
    }

    if (token && storedUser) {
      setUser(storedUser);
      setUserType(storedUserType);
      setSchoolIdState(storedSchoolId);
      // Extract rights from token
      const tokenRights = extractRights(token);
      setRights(tokenRights);
    }
    setLoading(false);
  }, []);

  // Rehydrate selected SchoolDTO when schoolId or userType changes (only if authenticated)
  useEffect(() => {
    if (!user || loading) return; // Wait for authentication
    
    const rehydrate = async () => {
      const id = schoolId || localStorage.getItem('schoolId');
      if (!id) {
        stateService.setSelectedSchool(null);
        setSelectedSchoolState(null);
        return;
      }
      try {
        const list = await authService.getAccessibleSchools();
        const found = list.find(s => s.id === id) || null;
        stateService.setSelectedSchool(found);
        setSelectedSchoolState(found);
      } catch {
        stateService.setSelectedSchool(null);
        setSelectedSchoolState(null);
      }
    };
    rehydrate();
  }, [schoolId, userType, user, loading]);

  const login = async (userName: string, password: string, schoolId?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.login(userName, password, schoolId);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('userType', response.userType);
      if (response.user.schoolId) {
        localStorage.setItem('schoolId', response.user.schoolId);
        setSchoolIdState(response.user.schoolId);
      } else {
        localStorage.removeItem('schoolId');
        setSchoolIdState(null);
      }
      setUser(response.user);
      setUserType(response.userType);
      // Extract rights from token
      const tokenRights = extractRights(response.token);
      setRights(tokenRights);
      return { success: true };
    } catch (e: any) {
        return {
        success: false,
        error: e
      };
    }
  };

  const logout = (): void => {
    authService.logout();
    setUser(null);
    setUserType(null);
    setSchoolIdState(null);
    setSelectedSchoolState(null);
    setRights([]);
    stateService.setSelectedSchool(null);
  };

  const setSchoolId = (id: string | null, school?: School | null) => {
    if (id) {
      localStorage.setItem('schoolId', id);
    } else {
      localStorage.removeItem('schoolId');
    }
    setSchoolIdState(id);
    // If school is provided, set it immediately; otherwise clear and let rehydration handle it
    if (school !== undefined) {
      setSelectedSchoolState(school);
      stateService.setSelectedSchool(school);
    } else {
      setSelectedSchoolState(null);
      stateService.setSelectedSchool(null);
    }
  };

  const value: AuthContextType = {
    user,
    userType,
    schoolId,
    selectedSchool,
    rights,
    login,
    logout,
    setSchoolId,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

